/**
 * Éditeur de visuel : photo + calques, dessinés sur un canvas pour que
 * l'export soit exactement ce qu'on voit. Positions en fraction du cadre,
 * comme dans l'app. Pas de choix de police : neuf styles fixes, repris de
 * l'app, et la couleur du réseau vient du serveur (/api/posts/decor).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';

function h() { return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` }; }

const POLICES: Array<[string, string, string]> = [
  ['Anton', new URL('../assets/fonts/Anton-Regular.ttf', import.meta.url).href, '400'],
  ['BebasNeue', new URL('../assets/fonts/BebasNeue-Regular.ttf', import.meta.url).href, '400'],
  ['ArchivoBlack', new URL('../assets/fonts/ArchivoBlack-Regular.ttf', import.meta.url).href, '400'],
  ['Pacifico', new URL('../assets/fonts/Pacifico-Regular.ttf', import.meta.url).href, '400'],
  ['Caveat', new URL('../assets/fonts/Caveat.ttf', import.meta.url).href, '400 700'],
  ['PlayfairDisplay', new URL('../assets/fonts/PlayfairDisplay.ttf', import.meta.url).href, '400 900'],
];
const NOMS_STYLES = ['Impact', 'Néon', 'Bandeau', 'Cerné', 'Surligné', 'Élégant', 'Manuscrit', 'Étiquette', 'Titre'];
const EMOJIS = ['🏡', '🔑', '🥂', '🎉', '❤️', '🔥', '✨', '👀', '📍', '📞', '✅', '⭐', '💰', '📈', '🤝', '☀️', '🌳', '🛋️', '🚪', '📸', '😍', '😄', '🙌', '👏', '💪', '🎯', '⚡', '💎', '🏆', '🌟'];
const BADGES_DEFAUT = ['NOUVEAUTÉ', 'VENDU', 'SOUS OFFRE', 'BAISSE DE PRIX', 'COUP DE CŒUR', 'EXCLUSIVITÉ', 'VISITE LIBRE', 'À SAISIR'];

interface Calque {
  type: 'texte' | 'emoji' | 'badge' | 'autocollant';
  contenu: string;
  style: number;
  dx: number; dy: number; echelle: number; rotation: number;
  image?: ImageBitmap;
  l?: number; h?: number; // emprise du dernier dessin, pour la sélection au clic
}
interface Decor {
  couleur: string;
  badges: string[];
  punchlines: Array<{ categorie: string; emoji?: string; textes: string[] }>;
  autocollants: Array<{ id: string; nom: string; images: string[] }>;
}

function decouper(ctx: CanvasRenderingContext2D, texte: string, largeur: number) {
  const lignes: string[] = [];
  for (const paragraphe of texte.split('\n')) {
    let ligne = '';
    for (const mot of paragraphe.split(/\s+/)) {
      const essai = ligne ? `${ligne} ${mot}` : mot;
      if (ctx.measureText(essai).width > largeur && ligne) { lignes.push(ligne); ligne = mot; } else ligne = essai;
    }
    lignes.push(ligne);
  }
  return lignes;
}

/** Dessine un bloc de texte centré sur l'origine et rend son emprise. */
function bloc(
  ctx: CanvasRenderingContext2D, texte: string, police: string, taille: number, largeurMax: number, interligne: number,
  options: { couleur: string; fond?: string; rayon?: number; px?: number; py?: number; ombre?: boolean; contour?: number; aligne?: CanvasTextAlign; filet?: string },
) {
  ctx.font = police;
  ctx.textBaseline = 'middle';
  const lignes = decouper(ctx, texte, largeurMax);
  const largeur = Math.max(...lignes.map(l => ctx.measureText(l).width));
  const hauteur = lignes.length * taille * interligne;
  const px = options.px || 0;
  const py = options.py || 0;
  const l = largeur + px * 2;
  const ht = hauteur + py * 2;

  if (options.fond) {
    ctx.save();
    ctx.fillStyle = options.fond;
    ctx.beginPath();
    ctx.roundRect(-l / 2, -ht / 2, l, ht, options.rayon || 0);
    ctx.fill();
    ctx.restore();
  }
  if (options.filet) {
    ctx.fillStyle = options.filet;
    ctx.fillRect(-l / 2, -ht / 2, taille * 0.18, ht);
  }

  ctx.save();
  ctx.textAlign = options.aligne || 'center';
  const x = options.aligne === 'left' ? -largeur / 2 : 0;
  if (options.ombre) { ctx.shadowColor = 'rgba(0,0,0,.6)'; ctx.shadowBlur = taille * 0.35; ctx.shadowOffsetY = taille * 0.06; }
  lignes.forEach((ligne, i) => {
    const y = -hauteur / 2 + taille * interligne * (i + 0.5);
    if (options.contour) {
      ctx.lineWidth = options.contour; ctx.lineJoin = 'round'; ctx.strokeStyle = '#000';
      ctx.strokeText(ligne, x, y);
    }
    ctx.fillStyle = options.couleur;
    ctx.fillText(ligne, x, y);
  });
  ctx.restore();
  return { l, h: ht };
}

function dessinerTexte(ctx: CanvasRenderingContext2D, texte: string, style: number, base: number, couleur: string) {
  const t = base * 0.062;
  const large = base * 0.68;
  const haut = texte.toUpperCase();
  switch (style % 9) {
    case 0: return bloc(ctx, haut, `${t * 1.25}px Anton`, t * 1.25, large, 1.05, { couleur: '#fff', ombre: true });
    case 1: return bloc(ctx, haut, `${t}px Anton`, t, large, 1.1, { couleur: '#fff', fond: couleur, rayon: t * 0.35, px: t * 0.6, py: t * 0.3 });
    case 2: return bloc(ctx, haut, `${t * 1.05}px BebasNeue`, t * 1.05, base * 0.86, 1.12, { couleur: '#fff', fond: 'rgba(0,0,0,.66)', filet: couleur, px: t * 0.65, py: t * 0.34, aligne: 'left' });
    case 3: return bloc(ctx, texte, `${t * 0.95}px ArchivoBlack`, t * 0.95, large, 1.14, { couleur: '#fff', contour: t * 0.13 });
    case 4: return bloc(ctx, texte, `${t * 0.8}px ArchivoBlack`, t * 0.8, large, 1.16, { couleur: '#111827', fond: '#fff', rayon: t * 0.2, px: t * 0.5, py: t * 0.26 });
    case 5: return bloc(ctx, texte, `700 ${t * 0.98}px PlayfairDisplay`, t * 0.98, large, 1.24, { couleur: '#fff', ombre: true });
    case 6: return bloc(ctx, texte, `700 ${t * 1.5}px Caveat`, t * 1.5, large, 1.08, { couleur: '#fff', ombre: true });
    case 7: return bloc(ctx, texte, `${t * 0.9}px Pacifico`, t * 0.9, large, 1.35, { couleur, fond: '#fff', rayon: t * 1.3, px: t * 0.75, py: t * 0.34 });
    default: return bloc(ctx, haut, `${t * 1.7}px BebasNeue`, t * 1.7, base * 0.9, 1, { couleur: '#fff', ombre: true });
  }
}

interface Props { photo: File; onFermer: () => void; onValider: (image: File) => void }

export default function EditeurVisuel({ photo, onFermer, onValider }: Props) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const fond = useRef<ImageBitmap | null>(null);
  const glisse = useRef<{ i: number; ox: number; oy: number } | null>(null);
  const [calques, setCalques] = useState<Calque[]>([]);
  const [choisi, setChoisi] = useState(-1);
  const [pret, setPret] = useState(false);
  const [decor, setDecor] = useState<Decor>({ couleur: '#7C3AED', badges: BADGES_DEFAUT, punchlines: [], autocollants: [] });
  const [onglet, setOnglet] = useState<'texte' | 'phrases' | 'badges' | 'emojis' | 'autocollants'>('texte');
  const [famille, setFamille] = useState('');
  const [saisie, setSaisie] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let annule = false;
    (async () => {
      // Le dessin attend les polices : sinon le premier rendu sort en police
      // système et l'export avec.
      await Promise.all(POLICES.map(async ([nom, url, poids]) => {
        try {
          const f = new FontFace(nom, `url(${url})`, { weight: poids });
          (document.fonts as any).add(await f.load());
        } catch { /* police indisponible : le navigateur retombe sur la sienne */ }
      }));
      const image = await createImageBitmap(photo);
      if (annule) return;
      fond.current = image;
      const c = canvas.current;
      if (c) {
        const ratio = Math.min(1, 1080 / Math.max(image.width, image.height));
        c.width = Math.round(image.width * ratio);
        c.height = Math.round(image.height * ratio);
      }
      setPret(true);
    })().catch(() => setErreur('Cette image ne peut pas être ouverte.'));
    fetch('/api/posts/decor', { headers: h() }).then(r => (r.ok ? r.json() : null)).then(d => {
      if (!d || annule) return;
      setDecor({ couleur: d.couleur || '#7C3AED', badges: d.badges?.length ? d.badges : BADGES_DEFAUT, punchlines: d.punchlines || [], autocollants: d.autocollants || [] });
      setFamille(d.autocollants?.[0]?.id || '');
    }).catch(() => { /* émojis et texte libre restent disponibles */ });
    return () => { annule = true; };
  }, [photo]);

  const dessiner = useCallback((avecSelection: boolean) => {
    const c = canvas.current;
    const ctx = c?.getContext('2d');
    if (!c || !ctx || !fond.current) return;
    ctx.drawImage(fond.current, 0, 0, c.width, c.height);
    const base = Math.min(c.width, c.height);
    calques.forEach((k, i) => {
      ctx.save();
      ctx.translate(k.dx * c.width, k.dy * c.height);
      ctx.rotate(k.rotation);
      ctx.scale(k.echelle, k.echelle);
      let emprise = { l: base * 0.2, h: base * 0.2 };
      if (k.type === 'texte') emprise = dessinerTexte(ctx, k.contenu, k.style, base, decor.couleur);
      else if (k.type === 'badge') emprise = bloc(ctx, k.contenu, `${base * 0.05}px Anton`, base * 0.05, base * 0.8, 1.1, { couleur: '#fff', fond: decor.couleur, rayon: base * 0.012, px: base * 0.03, py: base * 0.014 });
      else if (k.type === 'emoji') emprise = bloc(ctx, k.contenu, `${base * 0.14}px sans-serif`, base * 0.14, base, 1.15, { couleur: '#000' });
      else if (k.image) {
        const l = base * 0.3;
        const ht = l * (k.image.height / k.image.width);
        ctx.drawImage(k.image, -l / 2, -ht / 2, l, ht);
        emprise = { l, h: ht };
      }
      k.l = emprise.l * k.echelle;
      k.h = emprise.h * k.echelle;
      if (avecSelection && i === choisi) {
        ctx.strokeStyle = '#fff';
        ctx.lineWidth = 2 / k.echelle;
        ctx.setLineDash([8 / k.echelle, 6 / k.echelle]);
        ctx.strokeRect(-emprise.l / 2 - 6, -emprise.h / 2 - 6, emprise.l + 12, emprise.h + 12);
      }
      ctx.restore();
    });
  }, [calques, choisi, decor.couleur]);

  useEffect(() => { if (pret) dessiner(true); }, [pret, dessiner]);

  const point = (e: React.PointerEvent) => {
    const c = canvas.current as HTMLCanvasElement;
    const r = c.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const appui = (e: React.PointerEvent) => {
    const c = canvas.current as HTMLCanvasElement;
    const p = point(e);
    for (let i = calques.length - 1; i >= 0; i--) {
      const k = calques[i];
      if (Math.abs(p.x - k.dx) * c.width <= (k.l || 0) / 2 + 8 && Math.abs(p.y - k.dy) * c.height <= (k.h || 0) / 2 + 8) {
        setChoisi(i);
        glisse.current = { i, ox: p.x - k.dx, oy: p.y - k.dy };
        c.setPointerCapture(e.pointerId);
        return;
      }
    }
    setChoisi(-1);
  };

  const deplacement = (e: React.PointerEvent) => {
    const g = glisse.current;
    if (!g) return;
    const p = point(e);
    setCalques(l => l.map((k, i) => i === g.i ? { ...k, dx: Math.min(1, Math.max(0, p.x - g.ox)), dy: Math.min(1, Math.max(0, p.y - g.oy)) } : k));
  };

  const ajouter = (k: Partial<Calque> & Pick<Calque, 'type' | 'contenu'>) => {
    setChoisi(calques.length);
    setCalques(l => [...l, { style: 0, dx: 0.5, dy: 0.5, echelle: 1, rotation: 0, ...k }]);
  };

  const ajouterAutocollant = async (url: string) => {
    try {
      const r = await fetch(url, { headers: h() });
      if (!r.ok) throw new Error();
      ajouter({ type: 'autocollant', contenu: url, image: await createImageBitmap(await r.blob()), echelle: 1.1 });
    } catch { setErreur("Cet autocollant n'a pas pu être chargé."); }
  };

  const maj = (modif: Partial<Calque>) => setCalques(l => l.map((k, i) => i === choisi ? { ...k, ...modif } : k));

  const valider = () => {
    dessiner(false); // sans le cadre de sélection
    (canvas.current as HTMLCanvasElement).toBlob(b => {
      if (!b) { setErreur("L'image n'a pas pu être créée."); return; }
      onValider(new File([b], 'visuel.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  };

  const k = calques[choisi];
  const ongletBtn = (id: typeof onglet, nom: string) => (
    <button onClick={() => setOnglet(id)} className={`px-2.5 py-1 text-xs rounded-full ${onglet === id ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-700'}`}>{nom}</button>
  );
  const images = decor.autocollants.find(f => f.id === famille)?.images || [];

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl w-full max-w-5xl max-h-full overflow-y-auto p-4 grid md:grid-cols-[1fr_300px] gap-4">
        <div className="flex items-center justify-center bg-gray-900 rounded-xl min-h-[280px]">
          <canvas ref={canvas} onPointerDown={appui} onPointerMove={deplacement} onPointerUp={() => { glisse.current = null; }}
            className="max-w-full max-h-[75vh] touch-none cursor-move" />
        </div>

        <div className="space-y-3">
          {erreur && <p className="text-sm text-red-600">{erreur}</p>}
          <div className="flex flex-wrap gap-1.5">
            {ongletBtn('texte', 'Texte')}{ongletBtn('phrases', 'Phrases')}{ongletBtn('badges', 'Badges')}{ongletBtn('emojis', 'Émojis')}{ongletBtn('autocollants', 'Autocollants')}
          </div>

          <div className="max-h-56 overflow-y-auto">
            {onglet === 'texte' && (
              <div className="flex gap-2">
                <input value={saisie} onChange={e => setSaisie(e.target.value)} placeholder="Votre texte" className="flex-1 min-w-0 border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
                <button disabled={!saisie.trim()} onClick={() => { ajouter({ type: 'texte', contenu: saisie.trim() }); setSaisie(''); }} className="px-3 text-sm rounded-lg bg-violet-600 text-white disabled:opacity-50">Ajouter</button>
              </div>
            )}
            {onglet === 'phrases' && (decor.punchlines.length === 0
              ? <p className="text-sm text-gray-500">Aucune phrase disponible.</p>
              : decor.punchlines.map(cat => (
                <div key={cat.categorie} className="mb-2">
                  <p className="text-xs font-semibold text-gray-500">{cat.emoji} {cat.categorie}</p>
                  {cat.textes.map(t => <button key={t} onClick={() => ajouter({ type: 'texte', contenu: t })} className="block w-full text-left text-sm py-1 hover:text-violet-700">{t}</button>)}
                </div>
              )))}
            {onglet === 'badges' && <div className="flex flex-wrap gap-1.5">{decor.badges.map(b => <button key={b} onClick={() => ajouter({ type: 'badge', contenu: b })} style={{ background: decor.couleur }} className="px-2 py-1 text-xs font-bold text-white rounded">{b}</button>)}</div>}
            {onglet === 'emojis' && <div className="grid grid-cols-6 gap-1 text-2xl">{EMOJIS.map(e => <button key={e} onClick={() => ajouter({ type: 'emoji', contenu: e, echelle: 0.9 })}>{e}</button>)}</div>}
            {onglet === 'autocollants' && (decor.autocollants.length === 0
              ? <p className="text-sm text-gray-500">Aucun autocollant disponible.</p>
              : <>
                <select value={famille} onChange={e => setFamille(e.target.value)} className="w-full border border-gray-200 rounded-lg px-2 py-1 text-sm mb-2">
                  {decor.autocollants.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                </select>
                <div className="grid grid-cols-4 gap-1">{images.map(u => <Vignette key={u} url={u} onClick={() => ajouterAutocollant(u)} />)}</div>
              </>)}
          </div>

          {k && (
            <div className="pt-3 border-t border-gray-100 space-y-2">
              {k.type === 'texte' && (
                <>
                  <input value={k.contenu} onChange={e => maj({ contenu: e.target.value })} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm" />
                  <select value={k.style} onChange={e => maj({ style: Number(e.target.value) })} className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-sm">
                    {NOMS_STYLES.map((n, i) => <option key={n} value={i}>{n}</option>)}
                  </select>
                </>
              )}
              <label className="block text-xs text-gray-500">Taille<input type="range" min={0.3} max={3} step={0.05} value={k.echelle} onChange={e => maj({ echelle: Number(e.target.value) })} className="w-full accent-violet-600" /></label>
              <label className="block text-xs text-gray-500">Rotation<input type="range" min={-3.14} max={3.14} step={0.02} value={k.rotation} onChange={e => maj({ rotation: Number(e.target.value) })} className="w-full accent-violet-600" /></label>
              <button onClick={() => { setCalques(l => l.filter((_, i) => i !== choisi)); setChoisi(-1); }} className="text-xs text-red-500">Retirer cet élément</button>
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={onFermer} className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200">Annuler</button>
            <button disabled={!pret} onClick={valider} className="flex-1 px-3 py-2 text-sm font-semibold rounded-lg bg-violet-600 text-white disabled:opacity-50">Valider</button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Route protégée : la vignette passe par fetch pour porter le jeton. */
function Vignette({ url, onClick }: { url: string; onClick: () => void }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let cree: string | null = null;
    let annule = false;
    fetch(url, { headers: h() }).then(r => (r.ok ? r.blob() : null)).then(b => {
      if (!b || annule) return;
      cree = URL.createObjectURL(b);
      setSrc(cree);
    }).catch(() => { /* vignette vide */ });
    return () => { annule = true; if (cree) URL.revokeObjectURL(cree); };
  }, [url]);
  return <button onClick={onClick} className="aspect-square bg-gray-100 rounded">{src && <img src={src} alt="" className="w-full h-full object-contain" />}</button>;
}
